import { Ext } from "../../Doc";
import type { GuideProps } from "../Guide";

/**
 * The member guide, Russian translation of en.tsx. Uses the formal "вы" and the same terms as the
 * app's Russian texts (капсула, марка, ключ доступа, фраза восстановления). Keep it in step with
 * the English source.
 */
export function GuideRu({ limits: l, link }: GuideProps) {
  return (
    <>
      <h1>Как работает Vrelis</h1>
      <p className="lead">
        Vrelis — сервис для отправки зашифрованных сообщений, капсул времени, которые можно прочитать только с выбранного вами момента: всем участникам пилота или
        одному человеку. До этого момента капсулу не может прочитать никто: ни тот, кому вы написали, ни те, кто управляет Vrelis, ни вы сами.
      </p>
      <p className="warn">
        Это закрытый пилот. Скачайте капсулы, которые хотите сохранить (см. ниже), и сохраните фразу восстановления.
      </p>

      <h2>Почему блокировке можно доверять</h2>
      <p>
        Блокировка — это не обещание Vrelis. Это опубликованная криптография плюс публичный сигнал времени, который Vrelis не контролирует. Вот на чём она держится
        и во что вам пришлось бы поверить, чтобы она не сработала.
      </p>

      <h3>drand: публичные часы, которые подписывают время</h3>
      <ul>
        <li>
          <Ext href="https://drand.love"><b>drand</b></Ext> — это сеть, которую ведёт{" "}
          <Ext href="https://www.cloudflare.com/leagueofentropy/">League of Entropy</Ext>, группа независимых организаций (среди них Cloudflare, Protocol Labs, Kudelski
          Security, EPFL и University College London), которые вместе каждые 3 секунды публикуют случайное подписанное значение. Значения пронумерованы: раунд 1,
          раунд 2 и так далее, каждый для известного момента. Любой может прочитать их с нескольких публичных ретрансляторов; будущий раунд не может прочитать никто, потому что
          его ещё не существует.
        </li>
        <li>
          Каждый раунд подписывается пороговой криптографией (<Ext href="https://en.wikipedia.org/wiki/BLS_digital_signature">подписи BLS</Ext>): каждая организация хранит одну долю секретного ключа, а для подписи нужно
          большинство долей. Ни одна организация в одиночку и ни одно их меньшинство не может создать подпись раунда раньше времени. Сеть работает
          с 2020 года; цепочка, которую используем мы,{" "}
          <Ext href="https://api.drand.sh/v2/chains/52db9ba70e0cc0f6eaf7803dd07447a1f5477735fd3f661792ba94600c84e971/info">quicknet</Ext>, — с 2023 года.
          Её публичный ключ зашит в наш код, и каждый полученный сигнал проверяется по нему.
        </li>
        <li>
          <b>Шифрование с временной блокировкой</b> (<Ext href="https://eprint.iacr.org/2023/189">tlock</Ext>, опубликовано исследователями из Protocol Labs и Cloudflare
          в 2023 году) превращает эти часы в замок: сообщение
          шифруется на номер раунда, и подпись раунда, когда её опубликуют, — единственный ключ, который его открывает. Пока drand её не опубликует, этого ключа нет
          нигде в мире.
        </li>
      </ul>

      <h3>Как это использует Vrelis</h3>
      <ul>
        <li>
          Ваш браузер шифрует текст и ключи ваших файлов на раунд drand, соответствующий выбранному вами времени; при написании капсулы номер раунда виден до отправки.
          Личная капсула шифруется второй раз на ключ получателя, поэтому после назначенного времени открыть её может только он.
        </li>
        <li>
          Наш сервер получает только запечатанный результат и хранит его. У него нет ключа, который открывает капсулу: ни до назначенного времени, ни, для личной капсулы,
          после него. Утечка базы данных раскрыла бы шифротекст и метаданные, но никогда — сообщение.
        </li>
        <li>
          Когда приходит время, ваш браузер (или автономный открыватель) получает подпись раунда с одного из четырёх публичных ретрансляторов drand, проверяет её
          по зашитому публичному ключу и расшифровывает. Vrelis в этом шаге не участвует.
        </li>
        <li>
          Каждая капсула подписана ключом автора (<Ext href="https://en.wikipedia.org/wiki/Secp256k1">secp256k1</Ext>, такой же тип ключа, как у Bitcoin
          и <Ext href="https://nostr.com">Nostr</Ext>), поэтому изменённая капсула будет обнаружена. Ключ создаётся в вашем браузере из ваших 12 слов (
          <Ext href="https://github.com/bitcoin/bips/blob/master/bip-0039.mediawiki">BIP-39</Ext>) и никогда не покидает его в незашифрованном виде.
        </li>
      </ul>

      <h3>Если Vrelis исчезнет</h3>
      <ul>
        <li>
          Форматы открыты: капсула — это <Ext href="https://github.com/nostr-protocol/nips/blob/master/01.md">событие Nostr</Ext> (для личной —{" "}
          <Ext href="https://github.com/nostr-protocol/nips/blob/master/59.md">подарочная обёртка NIP-59</Ext>), содержимое которого — файл{" "}
          <Ext href="https://age-encryption.org">age</Ext> с разделом <Ext href="https://github.com/drand/tlock-js">tlock</Ext>. Любая программа, которая
          реализует эти открытые спецификации, может открыть ваши капсулы; наша для этого не нужна.
        </li>
        <li>
          <b>Скачать</b> сохраняет капсулы на вашем собственном диске вместе с автономным открывателем: одним HTML-файлом, который открывает их на вашем компьютере без
          сервера Vrelis, используя нужный сигнал drand и, для личной капсулы, ваш ключ. Полный экспорт можно открыть без vrelis.com, если
          нужный сигнал доступен и вы сохранили файлы и ключи, которые ему нужны.
        </li>
        <li>
          Если сама сеть drand когда-нибудь остановится раньше, чем выпустит нужный будущий сигнал, такие капсулы могут остаться запертыми навсегда. Скачивание
          заранее этот риск не устраняет. Капсуле, дата открытия которой уже прошла, всё равно нужен её точный проверенный сигнал: если этого сигнала нет в вашем
          архиве и его нельзя получить с ретранслятора, одна дата не позволит открыть её без сети. Храните полные экспорты и ключи, которые им нужны.
        </li>
      </ul>

      <h3>Во что пришлось бы поверить, чтобы блокировка не сработала</h3>
      <ul>
        <li>
          Что большинство организаций drand сговорятся и подпишут раунд раньше времени. Они независимы, находятся в разных странах, а цепочка публична, поэтому
          такую подпись увидели бы все. Это допущение о доверии, на котором стоит вся система.
        </li>
        <li>
          Что веб-страница лжёт. Страница — это код, который Vrelis отдаёт вашему браузеру; тот, кто контролирует этот код, мог бы прочитать текст до запечатывания или ваш
          ключ после разблокировки. Именно поэтому капсулы можно скачать и открыть без сети, отпечатки ключей показываются, чтобы вы сверяли их вне
          Vrelis, а после пилота планируется приложение для телефона, которое держит ключ вне веб-страницы.
        </li>
        <li>
          Что сегодняшняя криптография взломана. Криптографию на эллиптических кривых, которую используют и drand, и ваш ключ, могут взломать будущие{" "}
          <Ext href="https://en.wikipedia.org/wiki/Post-quantum_cryptography">квантовые компьютеры</Ext>.
          Для капсул, которые должны оставаться запечатанными десятилетиями, это реальный риск, который пилот не решает.
        </li>
      </ul>

      <h2>Как войти</h2>
      <ol>
        <li>
          Если администратор одобрил ваш адрес, зарегистрируйтесь с ним напрямую; иначе откройте ссылку из приглашения и используйте его адрес. Войдите с этим аккаунтом Google (Google сообщает Vrelis только ваше
          имя и email) или с паролем (мы отправим ссылку для подтверждения адреса). Позже можно добавить ключ доступа для входа.
        </li>
        <li>
          Vrelis предложит создать ваш <b>ключ</b> в браузере: вашу главную подпись, которая подписывает то, что вы отправляете, и открывает личные капсулы, отправленные вам. Вы
          можете пропустить этот шаг и создать ключ позже в разделе «Аккаунт», «Ключи»; без ключа вы читаете только публичные капсулы. Прежде чем появится ваша <b>фраза восстановления</b> (12 слов, которые
          восстанавливают ключ на любом устройстве), вас попросят убедиться, что никто не может увидеть или записать ваш экран.
        </li>
        <li>Запишите 12 слов на бумаге. Вас попросят ввести три из них обратно, а затем снова войти, чтобы подтвердить, что это вы: паролем или ключом доступа, либо через Google для самого первого ключа.</li>
        <li>
          Выберите PIN (не меньше {l.pinDigits} цифр) и создайте <Ext href="https://passkeys.dev/docs/intro/what-are-passkeys/">ключ доступа</Ext> (отпечаток пальца,
          лицо или PIN устройства). Вместе они защищают ключ на этом устройстве
          и копию, которую Vrelis хранит для переноса на новое устройство. Если ваш браузер не может так использовать ключ доступа, ключ остаётся в этом браузере без защиты,
          а копии на сервере нет; тогда фраза — единственный путь на другое устройство.
        </li>
      </ol>

      <h2>Ваша фраза восстановления</h2>
      <ul>
        <li>Храните её на бумаге в надёжном месте. Не фотографируйте её и не храните на компьютере или в менеджере паролей.</li>
        <li>
          Кто владеет 12 словами, тот <b>и есть</b> вы: он может подписывать от вашего имени и открывать ваши личные капсулы. Если они утекли, используйте <b>Сбросить ключ</b> в разделе «Аккаунт»,
          «Ключи»: вы вводите фразу, снова входите, и ключ отзывается навсегда; никто больше не сможет отправлять с ним или на него. Вы указываете, с какого времени его могли
          украсть, и капсулы, отправленные с ним с этого момента, помечаются «отозванный ключ». Затем вы создаёте новый ключ с новой фразой.
        </li>
        <li>
          Ваши прежние ключи можно положить в <b>связку ключей</b>, зашифрованную текущим ключом, чтобы капсулы, отправленные на старый ключ, по-прежнему открывались без старой фразы.
          Можно также хранить старую фразу самостоятельно и добавить её в связку ключей позже.
        </li>
        <li>
          Если вы потеряли устройство: войдите на другом и используйте ключ доступа и PIN (если ключ доступа синхронизируется) или фразу. Если вы забыли PIN: используйте
          фразу. После {l.pinAttempts} неверных PIN копия на сервере блокируется; фраза записывает новую.
        </li>
        <li>
          Если вы потеряете фразу <b>и</b> все устройства с вашим ключом, ваши личные капсулы не сможет открыть никогда и никто. Администратор может только выдать вам
          новую личность. Для отзыва ключа тоже нужна фраза, поэтому без неё ключ за вас отзывает администратор.
        </li>
      </ul>

      <h2>Как написать капсулу</h2>
      <ul>
        <li>
          <b>Всем в пилоте</b> (публичная) или <b>одному участнику</b> (личная). Публичную капсулу после назначенного времени может прочитать каждый участник; личную — только
          тот, кому вы написали, и вы, если оставите копию (включено по умолчанию).
        </li>
        <li>
          У каждой капсулы должна быть <b>тема</b>. Она никогда не шифруется: её сразу видит каждый, кто видит капсулу, и наш сервер тоже.
        </li>
        <li>
          <b>Вступление</b> необязательно: до {l.introChars} символов, которые можно прочитать до назначенного времени, щёлкнув по капсуле. У публичной капсулы
          его читают все, и наш сервер тоже. У личной его читает только получатель, своим ключом; сервер его не видит. Сообщение и его
          файлы в любом случае остаются запертыми.
        </li>
        <li>
          Кто отправил личную капсулу, запечатано для ключа получателя: он видит это, как только откроет капсулу своим ключом, задолго до её времени.
          Тот, у кого есть капсула, но нет этого ключа, видит только тему и время. Наш сервер знает, какой аккаунт её отправил. Он сообщает это только получателю, в зашифрованном для его ключа виде, чтобы приложение могло проверить, что ключ, подписавший капсулу, принадлежит этому аккаунту; иначе вместо имени оно показывает «Непроверенный отправитель».
        </li>
        <li>
          <b>Скрыть отправителя</b> (личные капсулы): в списках получателя вместо вашего имени отображается «Отправитель скрыт», пока он не щёлкнет по капсуле, поэтому никто,
          кто смотрит ему через плечо, не увидит, кто ему пишет. Сам получатель по-прежнему может увидеть, что это вы, ещё до назначенного времени.
        </li>
        <li>
          Выберите, когда капсула откроется: через 5 минут, час, день, неделю, год или в выбранную дату вплоть до 2125 года. Настоящее время открытия показывается до отправки; оно
          точно до нескольких секунд.
        </li>
        <li>
          Текст: до {l.publicText} для публичной капсулы, {l.privateText} для личной. Файлы: до {l.files} файлов и в сумме {l.fileBytes} на тарифе Free или {l.planFileBytes} с Legacy или Start. Фото
          уменьшаются и теряют метаданные, включая местоположение.
        </li>
        <li>
          <b>Запечатать и отправить</b> один раз запрашивает PIN и ключ доступа, чтобы подписать капсулу. После этого никто не может её изменить и никто не может прочитать её до
          назначенного времени.
        </li>
        <li>
          Прежде чем написать кому-то лично, проверьте его ключ: при написании капсулы показывается его отпечаток (16 символов). Если ключ изменился с тех пор, как этот браузер видел его
          в последний раз, вы получите предупреждение, и отправка будет ждать, пока вы не подтвердите с этим человеком вне Vrelis, что новый ключ действительно его.
        </li>
      </ul>

      <h2>Как читать</h2>
      <ul>
        <li>
          <b>Лента</b>: публичные капсулы. <b>Входящие</b>: личные капсулы для вас. <b>Отправленные</b>: ваши. <b>Отслеживаемые</b>: публичные капсулы, которых вы ждёте. Подпишитесь на
          автора, чтобы видеть только его капсулы.
        </li>
        <li>
          <b>Скрыть</b> участника: его публичные капсулы уходят из вашей Ленты на всех устройствах; для него ничего не меняется. <b>Заблокировать</b> участника: он больше
          не может класть личные капсулы в ваши Входящие (его об этом предупреждают до отправки); вы по-прежнему можете быть подписаны на него и читать его публичные капсулы.
          Его прежние капсулы переходят во «Входящие», в раздел «Заблокированные», и по-прежнему открываются. Обе функции есть в меню человека и на странице «Люди», где видно и тех, кто подписан на вас.
          Vrelis ручается только за то, что получает и доставляет: никто не может помешать кому-то зашифровать что-то на ваш публичный ключ вне Vrelis.
        </li>
        <li>
          Личная капсула ничего не показывает, пока вы её не откроете: <b>Открыть</b> запрашивает PIN, затем показывает, кто её отправил и когда она откроется, а текст — когда
          наступит это время.
        </li>
        <li>
          Ваш ключ остаётся заблокированным; каждое действие, которому он нужен, запрашивает PIN. Чтобы прочитать несколько капсул подряд, используйте <b>Разблокировать на … мин</b> в панели
          вверху. <b>Заблокировать</b> сразу скрывает всё личное; уход со страницы — тоже.
        </li>
        <li>
          В разделе <a href="/account">Аккаунт</a>, «Конфиденциальность»: «Показывать отправителей в моих списках только после щелчка по капсуле» делает каждую личную капсулу капсулой со скрытым отправителем,
          на всех устройствах.
        </li>
        <li>
          Списки показывают по 50 капсул; <b>Показать ещё</b> добавляет следующие. Vrelis фильтрует и сортирует их (по времени открытия или по времени, когда он их
          получил), поэтому фильтр или группа передаёт Vrelis идентификаторы выбранных вами участников, но никогда — саму группу или её название. Фильтр Входящих по отправителю требует
          вашего ключа, не учитывает капсулы со скрытым отправителем и отключён, пока отправители видны только после щелчка.
        </li>
        <li>
          <b>Поиск</b> смотрит на темы, вступления публичных капсул, авторов публичных капсул и тех, кому отправляли вы. Он никогда не смотрит на открытый текст
          (он есть только на вашем устройстве) и на то, кто отправил вам личную капсулу.
        </li>
      </ul>

      <h2>Конверт для ответа, CC и Переписка</h2>
      <ul>
        <li>
          Личное сообщение может содержать <b>конверт для ответа</b>: вы просите каждого получателя прислать вам один личный ответ, запечатанный для вашего ключа.
          Ответить можно, даже не будучи подписанным на вас. Вы выбираете, откроется ли каждый ответ во время, которое назначаете вы (после этого времени ответы не
          принимаются), или во время, которое выберет получатель. Вы оплачиваете капсулу и ответ заранее: 2 марки на получателя вместо 1. Первый ответ получателю ничего не стоит; второй стоит ему одну марку, и вы видите оба.
        </li>
        <li>
          <b>CC</b>: личное сообщение одному участнику может одновременно уйти и другим участникам, и каждый в списке видит, кто ещё его получил. Список
          запечатан внутри сообщения вместе с подписью отправителя; наш сервер его никогда не видит. Отправка группе без CC остаётся слепой: никто не видит,
          кто ещё её получил.
        </li>
        <li>
          Сообщения с конвертом для ответа и ответы на них находятся на странице <b>Переписка</b>, а не во Входящих или Отправленных: под каждым
          запросом вы видите ответы каждого получателя по порядку, запертые до своего времени, и кто оплатил каждый из них. Ответ уходит только отправителю.
        </li>
      </ul>

      <h2>Как хранить капсулы без Vrelis</h2>
      <ul>
        <li>
          <b>Скачать</b> на капсуле сохраняет её в файл; если есть вложения, это .zip с файлами и автономным открывателем. Выберите несколько капсул и используйте{" "}
          <b>Скачать выбранные</b>, чтобы получить один архив («Выбрать все показанные» берёт показанные капсулы).
        </li>
        <li>
          Автономный открыватель — один HTML-файл (<a href="/account">Аккаунт</a> или внутри .zip). Он открывает ваши капсулы на вашем собственном компьютере, личные — с вашей фразой. Он
          никогда не обращается к vrelis.com; он только запрашивает сигнал открытия у публичного сервиса времени drand, если в файле его ещё нет.
        </li>
        <li>
          «Включить открытый текст и файлы» кладёт открытый текст и файлы в скачанный файл без шифрования: любой, у кого есть этот файл, может их прочитать.
        </li>
      </ul>

      <h2>Это устройство</h2>
      <ul>
        <li>
          <b>Заблокировать</b>: убирает ключ из памяти. <b>Выйти</b>: завершает этот сеанс; ключ остаётся на этом устройстве. <b>Выйти на всех устройствах</b>:
          завершает ваши сеансы на всех устройствах. <b>Забыть это устройство</b>: удаляет ваш ключ и всё, что Vrelis сохранил в этом браузере.
        </li>
        <li>Вход действует {l.sessionDays} дн.</li>
      </ul>

      <h2>Что Vrelis может и чего не может видеть</h2>
      <ul>
        <li>
          <b>Vrelis не может прочитать</b> капсулу до её времени, личную капсулу — никогда (у него нет к ней ключа), а также ваш ключ, PIN или фразу.
        </li>
        <li>
          <b>Vrelis видит</b>: ваше имя и email (email видят владелец и администраторы), кто отправляет капсулу и кому, когда она отправлена, когда откроется публичная
          капсула, размеры и то, что в капсуле есть файлы. После назначенного времени публичную капсулу может прочитать каждый участник, а значит, и Vrelis.
        </li>
        <li>
          Связка ключей и копии вашего ключа хранятся только в зашифрованном виде; Vrelis не может их открыть.
        </li>
        <li>
          Подпись доказывает, каким ключом написана капсула. Капсула с пометкой «отозванный ключ» подписана ключом, о краже которого его владелец позже сообщил. То, что ключ принадлежит конкретному человеку, основано на списке участников и на имени, которое отправитель указал в
          капсуле. Сверяйте отпечатки с людьми, которым пишете лично.
        </li>
      </ul>

      <h2>Тарифы и марки</h2>
      <ul>
        <li>
          Одна <b>марка</b> оплачивает одну доставку одному получателю. Ваша собственная копия бесплатна. Free даёт марок в месяц: {l.freeStamps}, хранилище {l.freeStorage} и файлы до {l.fileBytes} на сообщение; Legacy
          (марок в месяц: {l.legacyStamps}, {l.planStorage}, {l.planFileBytes}) получили первые участники; Start давал бы марок в месяц: {l.planStamps}. Марки месяца не переносятся и возвращаются в
          первый день следующего месяца (UTC). <b>Оплата</b> показывает, что у вас есть, что осталось и что запланировано.
        </li>
        <li>
          Пока ничего не списывается и ничего нельзя купить: тарифы — это предварительный просмотр, а запрос включает вручную администратор. Уже отправленное остаётся доступным для чтения вам и тем, кому вы это отправили, что бы ни
          случилось с вашим тарифом или марками.
        </li>
        <li>
          Копия для себя необязательна и включена по умолчанию. Если её выключить, это сообщение сможет прочитать только получатель: вы сами не сможете его прочитать, даже после открытия.
        </li>
      </ul>

      <h2>Ограничения пилота</h2>
      <ul>
        <li>
          Не больше {l.daily} доставок в день (ваша собственная копия бесплатна; капсула группе считается по одной на участника). Загрузки: {l.uploadDaily} и {l.unusedFiles} неиспользованных файлов за 24 часа;
          файлы, не использованные в капсуле в течение 24 часов, удаляются.
        </li>
        <li>Вход через Google, по паролю или с ключом доступа. Без платежей и без публичной сети: капсулы остаются внутри пилота.</li>
        <li>
          Если пригласить адрес, у которого уже есть аккаунт, этому участнику вместо приглашения уйдёт запрос на связь; если он его примет, вы подпишетесь друг на друга. Ваш
          экран в обоих случаях выглядит одинаково, поэтому никто не может с помощью приглашений узнать, кто является участником.
        </li>
      </ul>

      <h2>Разработчикам</h2>
      <p>
        Сервисы могут читать и отправлять капсулы через <a href="/developers">API Vrelis</a>. Сервис получает собственный аккаунт по сервисному приглашению от
        администратора; для всех остальных это обычный участник. Он создаёт собственный ключ и сам подписывает каждую капсулу; его токены API могут читать и отправлять, но
        не могут управлять ключами или способами входа. Сервис можно заблокировать или скрыть, как любого другого участника.
      </p>

      <h2>О чём мы просим участников пилота</h2>
      <ol>
        <li>Отправьте одну публичную капсулу, которая откроется через час, и одну личную капсулу другому участнику, которая откроется через день.</li>
        <li>Прочитайте публичные капсулы после открытия и попробуйте прочитать одну до него: у вас не должно получиться.</li>
        <li>Кто-то один из вас: настройте ключ на втором устройстве и прочитайте там свои Входящие.</li>
        <li>Кто-то один из вас: скройте другого участника и проверьте, что его капсулы исчезли только из вашей Ленты.</li>
      </ol>

      <h2>Проблемы</h2>
      <p>
        Используйте <b>Сообщить о проблеме</b> внизу каждой страницы или напишите на {link}. Опишите, что вы сделали, чего ожидали и что произошло; снимок экрана помогает.
        Сервис, его ограничения и ответственность описаны в <a href="/terms">Условиях использования</a>.
      </p>
    </>
  );
}
